import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Grid,
  IconButton,
  Typography,
  Checkbox,
  Alert,
  MenuItem,
  Paper,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { createClient } from '../redux/slices/clientsSlice';
import { AppDispatch } from '../redux/store';
import AnefImportModal from './AnefImportModal';
import api from '../services/api';

interface ClientFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const steps = ['Type de client', 'Informations', 'Documents', 'Contact'];

const ClientFormModal: React.FC<ClientFormModalProps> = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientCredentials, setClientCredentials] = useState<{
    username: string;
    mot_de_passe_temporaire: string;
    info: string;
  } | null>(null);
  const [anefModalOpen, setAnefModalOpen] = useState(false);
  const [anefCredentials, setAnefCredentials] = useState<{username: string; password: string} | null>(null);
  const [anefDemarches, setAnefDemarches] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    type_client: 'particulier' as 'particulier' | 'entreprise',
    nom: '',
    prenom: '',
    raison_sociale: '',
    telephone_principal: '',
    est_numero_etranger: false,
    telephone_secondaire: '',
    email: '',
    adresse: '',
    ville: '',
    pays: '',
    notes: '',
    acces_espace_client: false,
    username_espace_client: '',
  });

  const [documents, setDocuments] = useState<Array<{
    type: string;
    numero: string;
    date_expiration: string;
    file?: File;
    filePreview?: string;
  }>>([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddDocument = () => {
    setDocuments([...documents, { type: '', numero: '', date_expiration: '' }]);
  };

  const handleRemoveDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleDocumentChange = (index: number, field: string, value: string) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index] = { ...updatedDocuments[index], [field]: value };
    setDocuments(updatedDocuments);
  };

  const handleAnefImport = (data: any) => {
    const clientData = data.clientData || data;
    const credentials = data.anefCredentials;
    const demarches = data.demarches || [];

    // Sauvegarder les identifiants ANEF pour les ajouter après création du client
    if (credentials) {
      setAnefCredentials(credentials);
    }

    // Sauvegarder les démarches sélectionnées
    if (demarches.length > 0) {
      setAnefDemarches(demarches);
    }

    // Pré-remplir le formulaire avec les données ANEF
    setFormData(prev => ({
      ...prev,
      type_client: clientData.type_client || 'particulier',
      nom: clientData.nom || '',
      prenom: clientData.prenom || '',
      raison_sociale: clientData.raison_sociale || '',
      telephone_principal: clientData.telephone_principal || '',
      est_numero_etranger: clientData.est_numero_etranger || false,
      telephone_secondaire: clientData.telephone_secondaire || '',
      email: clientData.email || '',
      adresse: clientData.adresse || '',
      ville: clientData.ville || '',
      pays: clientData.pays || '',
      notes: clientData.notes || '',
    }));

    // Pré-remplir les documents si disponibles
    const docs = [];
    if (clientData.numero_passeport) {
      docs.push({
        type: 'passeport',
        numero: clientData.numero_passeport,
        date_expiration: clientData.date_expiration_passeport || ''
      });
    }
    if (docs.length > 0) {
      setDocuments(docs);
    }

    setError('');
    setAnefModalOpen(false);
  };

  const handleFileChange = (index: number, file: File | null) => {
    if (!file) return;

    const updatedDocuments = [...documents];
    updatedDocuments[index] = {
      ...updatedDocuments[index],
      file: file,
      filePreview: URL.createObjectURL(file)
    };
    setDocuments(updatedDocuments);
  };

  const handleNext = () => {
    // Validation par étape
    if (activeStep === 0) {
      // Pas de validation pour le type
    } else if (activeStep === 1) {
      if (formData.type_client === 'particulier') {
        if (!formData.nom.trim()) {
          setError('Le nom est obligatoire');
          return;
        }
      } else {
        if (!formData.raison_sociale.trim()) {
          setError('La raison sociale est obligatoire');
          return;
        }
      }
      if (!formData.telephone_principal.trim()) {
        setError('Le téléphone principal est obligatoire');
        return;
      }
      if (formData.acces_espace_client && !formData.username_espace_client.trim()) {
        setError('Un nom d\'utilisateur est requis pour créer un espace client');
        return;
      }
    }

    setError('');
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Construire les données avec les documents
      const clientData: any = { ...formData };

      // Ajouter les documents spécifiques selon leur type
      documents.forEach((doc) => {
        if (doc.type === 'cni' && doc.numero) {
          clientData.numero_cni = doc.numero;
          clientData.date_expiration_cni = doc.date_expiration || null;
        } else if (doc.type === 'passeport' && doc.numero) {
          clientData.numero_passeport = doc.numero;
          clientData.date_expiration_passeport = doc.date_expiration || null;
        } else if (doc.type === 'carte_consulaire' && doc.numero) {
          clientData.numero_carte_consulaire = doc.numero;
          clientData.date_expiration_carte_consulaire = doc.date_expiration || null;
        }
      });

      const response: any = await dispatch(createClient(clientData)).unwrap();
      const clientId = response.id;

      // Si on a des identifiants ANEF, les ajouter dans la gestion de mot de passe
      if (anefCredentials && clientId) {
        try {
          await api.post(`/clients/${clientId}/password-management/passwords`, {
            label: 'Compte ANEF',
            username: anefCredentials.username,
            password: anefCredentials.password,
            note_verification: 'Identifiants importés automatiquement depuis ANEF',
            use_for_monitoring: false
          });
          console.log('Identifiants ANEF ajoutés à la gestion de mot de passe');
        } catch (err) {
          console.error('Erreur lors de l\'ajout des identifiants ANEF:', err);
          // Ne pas bloquer la création du client si l'ajout du mot de passe échoue
        }
      }

      // Si on a des démarches importées depuis ANEF, les créer
      if (anefDemarches.length > 0 && clientId) {
        try {
          const demarchesResponse = await api.post('/demarches/bulk', {
            client_id: clientId,
            demarches: anefDemarches
          });
          console.log(`${demarchesResponse.data.data.summary.success} démarche(s) importée(s) depuis ANEF`);
        } catch (err) {
          console.error('Erreur lors de la création des démarches ANEF:', err);
          // Ne pas bloquer la création du client si l'import des démarches échoue
        }
      }

      // Si un espace client a été créé, afficher les identifiants
      if (response.espace_client) {
        setClientCredentials(response.espace_client);
      } else {
        // Réinitialiser et fermer si pas d'espace client
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création du client');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
        type_client: 'particulier',
        nom: '',
        prenom: '',
        raison_sociale: '',
        telephone_principal: '',
        est_numero_etranger: false,
        telephone_secondaire: '',
        email: '',
        adresse: '',
        ville: '',
        pays: '',
        notes: '',
        acces_espace_client: false,
        username_espace_client: '',
      });
    setDocuments([]);
    setActiveStep(0);
    setClientCredentials(null);
    setAnefCredentials(null);
    setAnefDemarches([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCredentialsAcknowledged = () => {
    resetForm();
    if (onSuccess) onSuccess();
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                Sélectionnez le type de client
              </FormLabel>
              <RadioGroup
                name="type_client"
                value={formData.type_client}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="particulier"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>Particulier</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Client individuel (personne physique)
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}
                />
                <FormControlLabel
                  value="entreprise"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>Entreprise</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Client professionnel (personne morale)
                      </Typography>
                    </Box>
                  }
                  sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={2}>
              {formData.type_client === 'particulier' ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Prénom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Raison sociale"
                    name="raison_sociale"
                    value={formData.raison_sociale}
                    onChange={handleChange}
                    required
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone principal"
                  name="telephone_principal"
                  value={formData.telephone_principal}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone secondaire"
                  name="telephone_secondaire"
                  value={formData.telephone_secondaire}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="est_numero_etranger"
                      checked={formData.est_numero_etranger}
                      onChange={handleChange}
                    />
                  }
                  label="Numéro de téléphone étranger"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  helperText="Optionnel"
                />
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="acces_espace_client"
                        checked={formData.acces_espace_client}
                        onChange={handleChange}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Créer un espace client
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Le client pourra suivre ses démarches et factures en ligne
                        </Typography>
                      </Box>
                    }
                  />

                  {formData.acces_espace_client && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        label="Nom d'utilisateur"
                        name="username_espace_client"
                        value={formData.username_espace_client}
                        onChange={handleChange}
                        placeholder="Ex: jdupont123"
                        helperText="Le client utilisera ce nom d'utilisateur pour se connecter"
                        size="small"
                        required
                      />
                      {formData.acces_espace_client && !formData.username_espace_client && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                          Un nom d'utilisateur est requis pour créer un espace client
                        </Alert>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Documents d'identité (optionnel)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddDocument}
              >
                Ajouter un document
              </Button>
            </Box>

            {documents.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Aucun document ajouté
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cliquez sur "Ajouter un document" pour commencer
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {documents.map((doc, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Document {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveDocument(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Type de document"
                          value={doc.type}
                          onChange={(e) => handleDocumentChange(index, 'type', e.target.value)}
                          size="small"
                        >
                          <MenuItem value="cni">Carte Nationale d'Identité (CNI)</MenuItem>
                          <MenuItem value="passeport">Passeport</MenuItem>
                          <MenuItem value="carte_consulaire">Carte Consulaire</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Numéro du document"
                          value={doc.numero}
                          onChange={(e) => handleDocumentChange(index, 'numero', e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Date d'expiration"
                          type="date"
                          value={doc.date_expiration}
                          onChange={(e) => handleDocumentChange(index, 'date_expiration', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            border: '2px dashed',
                            borderColor: doc.file ? 'success.main' : 'divider',
                            borderRadius: 1,
                            p: 2,
                            textAlign: 'center',
                            bgcolor: doc.file ? 'success.50' : 'grey.50',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'primary.50',
                            },
                          }}
                          onClick={() => document.getElementById(`file-input-${index}`)?.click()}
                        >
                          <input
                            id={`file-input-${index}`}
                            type="file"
                            accept="image/*,.pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileChange(index, file);
                            }}
                          />
                          {doc.file ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                {doc.file.type.startsWith('image/') ? (
                                  <ImageIcon color="success" />
                                ) : (
                                  <AttachFileIcon color="success" />
                                )}
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  Fichier chargé
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {doc.file.name} ({(doc.file.size / 1024).toFixed(1)} KB)
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Cliquez pour remplacer
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                              <Typography variant="body2" color="text.secondary">
                                Cliquez pour télécharger le document
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                PDF ou Image (JPG, PNG)
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ville"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pays"
                  name="pays"
                  value={formData.pays}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  // Dialog pour afficher les identifiants de l'espace client
  if (clientCredentials) {
    return (
      <Dialog
        open={open}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
            <Typography variant="h6" component="div" fontWeight={700}>
              Espace client créé !
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            {clientCredentials.info}
          </Alert>

          <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Identifiants de connexion :
            </Typography>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nom d'utilisateur"
                value={clientCredentials.username}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Mot de passe temporaire"
                value={clientCredentials.mot_de_passe_temporaire}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Box>
          </Paper>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Important :
            </Typography>
            <Typography variant="body2">
              Notez ces identifiants avant de fermer cette fenêtre. Vous ne pourrez plus les consulter après.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={handleCredentialsAcknowledged}
            fullWidth
          >
            J'ai noté les identifiants
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" fontWeight={700}>
            Nouveau client
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAnefModalOpen(true)}
              startIcon={<CloudDownloadIcon />}
              sx={{ textTransform: 'none' }}
            >
              Importer depuis ANEF
            </Button>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Stepper activeStep={activeStep} sx={{ px: 3, pt: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            startIcon={<BackIcon />}
            disabled={loading}
          >
            Précédent
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<NextIcon />}
            disabled={loading}
          >
            Suivant
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Création...' : 'Créer le client'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <AnefImportModal
      open={anefModalOpen}
      onClose={() => setAnefModalOpen(false)}
      onImportSuccess={handleAnefImport}
    />
  </>
  );
};

export default ClientFormModal;
